import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactSelect from 'react-select';
import { toast } from 'react-toastify';
import {
  ArrowPathIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { HelpTitle } from '../../components/Help/HelpButton';
import TestsGuide from '../../components/Help/guides/TestsGuide';
import OverflowMenu from '../../components/ui/OverflowMenu';
import usePassagesAvailable from '../../components/Passages/usePassagesAvailable';
import { getTests, deleteTest } from '../../services/Exams/ExamsService';
import { getEntityLabel } from '../../utils/entityLabel';
import { useRowLink } from '../../utils/rowLink';
import { getCodedErrorMessage } from '../../utils/apiError';
import { fetchAllPages } from '../../utils/fetchAllPages';
import { isCanceledError } from '../../utils/examError';
import { formatDate } from '../../utils/formatDate';
import FilterChips, { CHIP_TONES } from '../../components/QuestionList/FilterChips';
import FilterPanel from '../../components/QuestionList/FilterPanel';
import ResultsBar, { Pager } from '../../components/QuestionList/ResultsBar';
import { filterSelectStyles, selectTheme } from '../../components/forms/Select/styles';
import { focusRing, Spinner, useMediaQuery } from './TestBuilder/ui';

// The whole list is loaded (the filters and the sort work on it here), then shown by pages of this size;
// search results come all on one page, to scroll through.
const PAGE_SIZE = 30;

// A test opens on its page, which comes back to this list.
const testHref = (test) => `/test/${test.uuid}`;
const OPEN_STATE = { from: '/test_list' };

// The category's name ('' for a test without one): the category filter compares these names.
const categoryOf = (test) => getEntityLabel(test?.category, '');

const createdTime = (test) => {
  const time = Date.parse(test?.created);
  return Number.isNaN(time) ? 0 : time;
};

// [ordering, label key]; newest first is the default, as the server sends the list.
const SORTS = [
  ['-created', 'newest'],
  ['created', 'oldest'],
  ['name', 'name_asc'],
  ['-name', 'name_desc'],
];

// The page's one main button (the width of the screen on phones).
const primaryButtonClass = `inline-flex h-11 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 hover:bg-blue-700 max-md:w-full md:h-10 ${focusRing}`;
const secondaryButtonClass = `mt-4 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-white px-3 text-sm font-medium text-slate-700 shadow-sm ring-1 ring-inset ring-slate-300 transition duration-200 hover:bg-slate-50 hover:text-slate-900 ${focusRing}`;
const stateCardClass =
  'flex flex-col items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-16 text-center shadow-sm';
const headerCell = (align) => `px-4 py-3 ${align} text-xs font-semibold uppercase tracking-wider text-slate-500`;

// A test has no status, only is_active (same labels as the exam's "add existing test" list).
function TestStatus({ active }) {
  const [t] = useTranslation();
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${
        (active ? CHIP_TONES.green : CHIP_TONES.slate).badge
      }`}
    >
      {active ? t('exam_bank.status.active') : t('exam_bank.status.inactive')}
    </span>
  );
}

// The category as a muted line under the name. (relative: the sr-only label is positioned absolutely;
// without it, inside the scrolling table it escapes the scroll box and widens the page.)
function CategoryLine({ category }) {
  const [t] = useTranslation();
  return (
    <p className="relative mt-1 flex min-w-0 items-center gap-1 text-xs text-slate-500" title={category}>
      <TagIcon className="h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden="true" />
      <span className="sr-only">{t('test_bank.list.columns.category')}: </span>
      <span className="truncate">{category}</span>
    </p>
  );
}

function TestName({ test, className = 'font-medium' }) {
  return (
    <Link
      to={testHref(test)}
      state={OPEN_STATE}
      className={`rounded text-slate-900 transition-colors duration-200 hover:text-blue-700 ${focusRing} ${className}`}
    >
      {test.name || '—'}
    </Link>
  );
}

// A test's rare action, its deletion (still confirmed), in "⋯"; the test opens from its row (its name is the
// link). While it is being deleted, a spinner stands in the menu's place. `portal`: inside the table's scroll box.
function TestActions({ test, deleting, onDelete, portal = false }) {
  const [t] = useTranslation();
  if (deleting) {
    return (
      <span role="status" className="inline-flex h-11 w-11 shrink-0 items-center justify-center md:h-9 md:w-9">
        <Spinner />
        <span className="sr-only">{t('test_bank.list.deleting')}</span>
      </span>
    );
  }
  return (
    <OverflowMenu
      portal={portal}
      label={test.name ? t('test_bank.common.test_actions_named', { name: test.name }) : t('test_bank.common.test_actions')}
      items={[{ key: 'delete', label: t('test_bank.list.delete_test'), Icon: TrashIcon, danger: true, onClick: () => onDelete(test) }]}
    />
  );
}

export default function TestList() {
  const [t, i18n] = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const rowLink = useRowLink();
  // A test's row or card opens it; a click in its "⋯" menu does not: the table's, drawn over the page (outside the
  // row, but inside it for React's events), nor the card's, inside it (a tap in the menu's padding hits no item);
  // nor one on the spinner that stands in for "⋯" while the test is being deleted.
  const openTest = (test) => {
    const { onClick, onAuxClick } = rowLink(testHref(test), { state: OPEN_STATE });
    const inRow = (event) => {
      const { target, currentTarget } = event;
      if (!(target instanceof Node)) return true;
      if (!currentTarget.contains(target)) return false;
      const actions = target instanceof Element ? target.closest('[role="menu"], [role="status"]') : null;
      return !actions || !currentTarget.contains(actions);
    };
    return {
      onClick: (event) => inRow(event) && onClick(event),
      onAuxClick: (event) => inRow(event) && onAuxClick(event),
    };
  };
  // Phones get the list as cards instead of the wide table.
  const wide = useMediaQuery('(min-width: 768px)');
  const passages = usePassagesAvailable();
  const resultsRef = useRef(null);

  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [search, setSearch] = useState('');
  // "all", "active" or "inactive"
  const [status, setStatus] = useState('all');
  // A category's name; '' = every category.
  const [categoryFilter, setCategoryFilter] = useState('');
  const [ordering, setOrdering] = useState('-created');
  const [page, setPage] = useState(1);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadFailed(false);
    // Newest first, so a test just created is at the top; every page of the list, not only the first.
    fetchAllPages((pageParams) =>
      getTests({ params: { ...pageParams, ordering: '-created' }, signal: controller.signal })
    )
      .then((data) => setTests(Array.isArray(data) ? data : []))
      .catch((err) => {
        if (isCanceledError(err)) return;
        console.error('Error fetching tests:', err);
        setTests([]);
        setLoadFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const collator = useMemo(() => {
    try {
      return new Intl.Collator(language, { numeric: true, sensitivity: 'base' });
    } catch (e) {
      return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    }
  }, [language]);

  // Only the categories the loaded tests have.
  const categories = useMemo(
    () => [...new Set(tests.map(categoryOf).filter(Boolean))].sort(collator.compare),
    [tests, collator]
  );
  // A category no test has any more (its last test deleted) is neither offered nor applied.
  const category = categories.includes(categoryFilter) ? categoryFilter : '';
  const query = search.trim().toLowerCase();
  const searching = query !== '';

  // The search and the category; the status chips count inside them.
  const matching = useMemo(
    () =>
      tests.filter(
        (test) =>
          (!searching ||
            test.name?.toLowerCase().includes(query) ||
            test.description?.toLowerCase().includes(query)) &&
          (!category || categoryOf(test) === category)
      ),
    [tests, searching, query, category]
  );
  const activeCount = matching.filter((test) => test.is_active).length;

  const shown = useMemo(() => {
    const list =
      status === 'all' ? matching : matching.filter((test) => (status === 'active' ? test.is_active : !test.is_active));
    const byName = (a, b) => collator.compare((a.name || '').trim(), (b.name || '').trim());
    const compare = {
      '-created': (a, b) => createdTime(b) - createdTime(a),
      created: (a, b) => createdTime(a) - createdTime(b),
      name: byName,
      '-name': (a, b) => byName(b, a),
    }[ordering];
    return compare ? [...list].sort(compare) : list;
  }, [matching, status, ordering, collator]);

  // A page left empty (its last test deleted, or fewer results) shows the last page there is.
  const pageCount = searching ? 1 : Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageTests = searching ? shown : shown.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // A new search, filter or sort starts from the first page.
  const changeSearch = (value) => {
    setSearch(value);
    setPage(1);
  };
  const changeStatus = (value) => {
    setStatus(value);
    setPage(1);
  };
  const changeCategory = (value) => {
    setCategoryFilter(value);
    setPage(1);
  };
  const changeOrdering = (value) => {
    setOrdering(value);
    setPage(1);
  };
  // Every filter back to "all"; the sort order stays.
  const clearAll = () => {
    setSearch('');
    setStatus('all');
    setCategoryFilter('');
    setPage(1);
  };
  // The pager under the list brings the top of the new page into view.
  const changePageFromBottom = (next) => {
    setPage(next);
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    resultsRef.current?.scrollIntoView({ block: 'start', behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  const handleDelete = async (test) => {
    // A test students have taken is refused (409 test_has_attempts: their answers stay with it); the toast says why.
    if (!window.confirm(t('test_bank.list.confirm_delete_warning'))) {
      return;
    }
    setDeletingId(test.uuid);
    try {
      await deleteTest({ params: { uuid: test.uuid } });
      setTests((prev) => prev.filter((item) => item.uuid !== test.uuid));
      toast.success(t('test_bank.list.deleted'));
    } catch (err) {
      console.error('Error deleting test:', err);
      toast.error(getCodedErrorMessage(t, err, t('test_bank.list.delete_error')));
    } finally {
      setDeletingId(null);
    }
  };

  const filtersActive = searching || status !== 'all' || !!category;

  const categoryOptions = [
    { value: '', label: t('test_bank.filters.all_categories') },
    ...categories.map((name) => ({ value: name, label: name })),
  ];
  // Shown only when some test has a category.
  const filters =
    categories.length > 0
      ? [
          {
            key: 'category',
            active: !!category,
            node: (
              <ReactSelect
                aria-label={t('test_bank.list.columns.category')}
                styles={filterSelectStyles}
                theme={selectTheme}
                options={categoryOptions}
                value={categoryOptions.find((option) => option.value === category) || categoryOptions[0]}
                onChange={(option) => changeCategory(option?.value || '')}
                isSearchable={categoryOptions.length > 8}
                noOptionsMessage={() => t('table.no_options')}
              />
            ),
          },
        ]
      : [];

  const header = (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        {/* The guide's "?" right after the title. */}
        <HelpTitle helpId="tests" helpTitle={t('help.tests.title')} guide={<TestsGuide passages={passages} />}>
          {t('nav.test_bank')}
        </HelpTitle>
        <p className="mt-1 text-sm text-slate-500">{t('test_bank.list.page_subtitle')}</p>
      </div>
      <Link to="/test_create/" className={primaryButtonClass}>
        <PlusIcon className="h-5 w-5" aria-hidden="true" />
        {t('test_bank.list.create_test')}
      </Link>
    </div>
  );

  let body;
  if (loading) {
    body = (
      <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 shadow-sm" role="status">
        <div className="text-center">
          <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600" aria-hidden="true" />
          <p className="text-sm text-slate-500">{t('test_bank.list.loading')}</p>
        </div>
      </div>
    );
  } else if (loadFailed) {
    body = (
      <div className={stateCardClass} role="alert">
        <div className="mb-3 rounded-full bg-red-50 p-3 text-red-500">
          <ExclamationTriangleIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="text-sm font-medium text-red-700">{t('test_bank.list.load_error')}</h3>
        <button type="button" onClick={() => setReloadKey((key) => key + 1)} className={secondaryButtonClass}>
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t('test_bank.builder.retry')}
        </button>
      </div>
    );
  } else if (tests.length === 0) {
    body = (
      <div className={stateCardClass}>
        <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
          <DocumentTextIcon className="h-6 w-6" aria-hidden="true" />
        </div>
        <h3 className="text-sm font-medium text-slate-900">{t('test_bank.list.empty')}</h3>
        <p className="mt-1 text-sm text-slate-500">{t('test_bank.list.empty_hint')}</p>
      </div>
    );
  } else {
    body = (
      <>
        <FilterChips
          label={t('question_list.status_filter')}
          allLabel={t('question_list.status_all')}
          allCount={matching.length}
          items={[
            { value: 'active', label: t('exam_bank.status.active'), count: activeCount, tone: CHIP_TONES.green },
            {
              value: 'inactive',
              label: t('exam_bank.status.inactive'),
              count: matching.length - activeCount,
              tone: CHIP_TONES.slate,
            },
          ]}
          selected={status}
          onSelect={changeStatus}
        />

        <FilterPanel
          search={search}
          onSearchChange={changeSearch}
          searchPlaceholder={t('test_bank.list.search_name_description')}
          searchLabel={t('test_bank.list.search_label')}
          filters={filters}
          canClear={filtersActive}
          onClearAll={clearAll}
        />

        <div ref={resultsRef} className="scroll-mt-4">
          <ResultsBar
            countText={t('test_bank.list.count', { count: shown.length })}
            page={currentPage}
            pageCount={pageCount}
            ordering={ordering}
            onOrderingChange={changeOrdering}
            sortOptions={SORTS.map(([value, key]) => ({ value, label: t(`question_list.sort_options.${key}`) }))}
            onPage={setPage}
          />
        </div>

        {shown.length === 0 ? (
          <div className={stateCardClass}>
            <div className="mb-3 rounded-full bg-slate-100 p-3 text-slate-400">
              <MagnifyingGlassIcon className="h-6 w-6" aria-hidden="true" />
            </div>
            <h3 className="text-sm font-medium text-slate-900">{t('test_bank.list.not_found')}</h3>
            <p className="mt-1 text-sm text-slate-500">{t('test_bank.list.no_results')}</p>
            {filtersActive && (
              <button type="button" onClick={clearAll} className={secondaryButtonClass}>
                {t('question_list.clear_all')}
              </button>
            )}
          </div>
        ) : wide ? (
          // relative: the sr-only texts inside stay in the scroll box.
          <div className="relative min-w-0 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50">
                <tr>
                  <th scope="col" className={headerCell('text-left')}>
                    {t('test_bank.list.columns.test')}
                  </th>
                  <th scope="col" className={headerCell('text-left')}>
                    {t('test_bank.list.columns.status')}
                  </th>
                  <th scope="col" className={headerCell('text-right')}>
                    {t('test_bank.list.columns.questions')}
                  </th>
                  <th scope="col" className={headerCell('text-left')}>
                    {t('test_bank.list.columns.created')}
                  </th>
                  <th scope="col" className="relative px-4 py-3">
                    <span className="sr-only">{t('test_bank.list.columns.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {pageTests.map((test) => {
                  const testCategory = categoryOf(test);
                  const questions = test.questions_count || 0;
                  return (
                    <tr
                      key={test.uuid}
                      {...openTest(test)}
                      className="cursor-pointer transition-colors duration-150 hover:bg-slate-50/80"
                    >
                      <td className="px-4 py-3 text-sm">
                        <div className="min-w-[16rem] max-w-3xl">
                          <TestName test={test} />
                          {test.description && (
                            <p className="mt-0.5 line-clamp-2 text-sm text-slate-500" title={test.description}>
                              {test.description}
                            </p>
                          )}
                          {testCategory && <CategoryLine category={testCategory} />}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <TestStatus active={test.is_active} />
                      </td>
                      <td
                        className={`whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums ${
                          questions ? 'text-slate-700' : 'text-slate-500'
                        }`}
                      >
                        {questions}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-500">
                        {formatDate(test.created, language)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <div className="flex items-center justify-end">
                          <TestActions test={test} deleting={deletingId === test.uuid} onDelete={handleDelete} portal />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <ul className="flex flex-col gap-2.5">
            {pageTests.map((test) => {
              const questions = test.questions_count || 0;
              const testCategory = categoryOf(test);
              const created = formatDate(test.created, language);
              return (
                <li
                  key={test.uuid}
                  {...openTest(test)}
                  className="flex cursor-pointer items-start gap-2 rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm transition duration-200 hover:border-slate-300"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <TestName test={test} className="min-w-0 text-[15px] font-semibold leading-snug [overflow-wrap:anywhere]" />
                      <TestStatus active={test.is_active} />
                    </div>
                    {test.description && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{test.description}</p>}
                    {testCategory && <CategoryLine category={testCategory} />}
                    {/* An empty test's count steps back, as in the table. */}
                    <p className="mt-1.5 text-sm text-slate-600">
                      <span className={questions ? '' : 'text-slate-500'}>{t('question_list.count', { count: questions })}</span>
                      {created && ` · ${created}`}
                    </p>
                  </div>
                  {/* "⋯" in the card's corner, beside the name. */}
                  <div className="-mr-1.5 -mt-1.5 shrink-0">
                    <TestActions test={test} deleting={deletingId === test.uuid} onDelete={handleDelete} />
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {shown.length > 0 && (
          <div className="flex justify-center">
            <Pager
              page={currentPage}
              pageCount={pageCount}
              onPage={changePageFromBottom}
              label={t('question_list.pagination_bottom')}
            />
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      {header}
      {body}
    </div>
  );
}
